import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";

type QA = { q: string; a: string };

const FAQS: QA[] = [
  {
    q: "How fast will I see meetings?",
    a: "Most accounts see their first qualified meeting request within 10 to 21 days. The exact timing depends on inbox warm-up, list quality, and how clearly your ICP is defined during onboarding.",
  },
  {
    q: "What if I don't have a prospect list?",
    a: "You don't need one. AutoPitch builds your list from your ICP — industries, titles, regions, company size — and enriches each contact with the signals that make a good opener.",
  },
  {
    q: "Will the emails actually sound like me?",
    a: "Yes. During training, AutoPitch ingests your website, sales material, objections, and tone preferences. Every send is grounded in your business, not generic SaaS copy.",
  },
  {
    q: "How is this different from Smartlead or Apollo?",
    a: "Smartlead and Apollo give you sending and lists. AutoPitch is the full AI SDR — it picks the prospect, writes the message, replies to objections, books the meeting, and shows you the reasoning behind every decision.",
  },
  {
    q: "What does 'first qualified meeting free' mean?",
    a: "You only pay when AutoPitch books your first qualified meeting. No credit card to start. Pricing is outcome-based at $49/month after that — anchored to results, not email volume.",
  },
  {
    q: "Can I pause or edit anything?",
    a: "Always. You can pause campaigns, edit drafts before they send, approve replies, change ICP rules, or override AI decisions at any time. Nothing leaves your account without your control.",
  },
  {
    q: "Do you support languages other than English?",
    a: "Yes. AutoPitch sends native outreach in English, Spanish, Brazilian Portuguese, and German — with cultural adaptation, not direct translation.",
  },
  {
    q: "Is my data safe?",
    a: "Your data is isolated by tenant with row-level security. We honor GDPR and CAN-SPAM, include RFC-8058 one-click unsubscribe on every send, and maintain an audited suppression list.",
  },
  {
    q: "Do I need my own sending domain?",
    a: "We recommend it for deliverability. AutoPitch helps you provision and warm a separate sending domain so your outbound never affects your transactional mail.",
  },
  {
    q: "What happens if I want to stop?",
    a: "Cancel anytime. No contracts, no minimums. Your data, prospects, and conversations stay accessible.",
  },
  {
    q: "Does the AI ever invent links, case studies, or numbers?",
    a: "No. Auto Pitch can only reference assets you've approved in your Sales Library, by exact title. It cannot fabricate a Loom URL, a customer logo, or a metric. Every reference is logged and auditable on the conversation timeline.",
  },
  {
    q: "Do I really get a real branded booking page?",
    a: "Yes — generated for you on signup, before you invite a teammate. A calendar-synced booking page with an auto-generated hero banner, branded to your business. Edit anytime.",
  },
  {
    q: "Where do my sales materials come from?",
    a: "You add them. The Sales Library lets you upload PDFs, decks, and case studies, or draft them from a URL. Auto Pitch only references assets you've approved — by exact title — so it can never hallucinate collateral.",
  },
];

export function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="space-y-10">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <Reveal stagger={60} className="text-center space-y-3 max-w-2xl mx-auto">
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          Frequently Asked
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
          Answers <span className="text-gradient-brand">before you ask</span>
        </h2>
      </Reveal>

      <Reveal stagger={40} className="max-w-3xl mx-auto space-y-2">
        {FAQS.map((faq, i) => {
          const isOpen = open === i;
          return (
            <div
              key={faq.q}
              className="rounded-xl border border-border bg-card/60 backdrop-blur-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-card/80"
                aria-expanded={isOpen}
              >
                <span className="text-sm font-semibold text-foreground">{faq.q}</span>
                <ChevronDown
                  className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <div className="px-5 pb-4 -mt-1">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </Reveal>
    </div>
  );
}
